// src/navigation/navConfig.js
//
// Single source of truth for navigation. Config + helpers only (no React).
//
// "One person, one home" (Jill approved 2026-07-02): there are no dashboards
// to switch between. Each login gets ONE nav assembled from role + position:
//   tabs       — the always-visible bar (bottom tab bar on phone/iPad,
//                top bar on desktop). Primary tools only.
//   moreGroups — everything else, in ONE "More" menu, grouped with labels.
//                Board / committee positions add ITEMS here (with attention
//                dots), never a separate world — positions are subordinate
//                to being a pilot.
//   homePath   — where "/" lands you (straight to work, no hub page).
//
// icon: a key TopNav maps to a lucide icon. attention: a key TopNav resolves
// to a live red dot / count (see useAttention there).
//
// Gating mirrors the routes: role-only items are stable; position-gated items
// (board / training chair / CE lead) and office capabilities (reservations /
// scheduler / accounting) only appear when held. This is presentation only —
// ProtectedRoute still enforces access.

import { COMMITTEE_POSITIONS } from "../lib/meetingFinder.js";
import { helpVideosFor } from "../data/helpVideos.js";
import { helpGuidesFor } from "../data/helpGuides.js";

export function buildNav({ roles = [], boardPosition = null, committeePositions = [], isTrainingPilot = false, hasTrainingTrack = false, isMentor = false } = {}) {
  const r = (roles || []).map((x) => String(x).toLowerCase());
  const pos = String(boardPosition || "").toLowerCase();
  const has = (role) => r.includes(role);

  const isAdmin = has("system_admin");
  // Board of Directors = President, VP, Secretary, Board Member (not committee roles).
  const isBoard = ["president", "vice_president", "secretary", "member"].includes(pos);
  const cp = (committeePositions || []).map((x) => String(x).toLowerCase());
  // committee positions come only from the roster checkbox (committeePositions);
  // a board_position naming one grants nothing (Jill 2026-10-07)
  const isChair = cp.includes("training_chair");
  const isCeAdmin = ["ce_lead", "ce_organizer"].includes(pos)
    || cp.some((p) => ["training_chair", "ce_organizer"].includes(p));

  let tabs = [];
  const moreGroups = [];
  let homePath = "/hub"; // fallback for a login with no recognized role
  let primary = null;

  if (has("pilot") || isAdmin) {
    primary = "pilot";
    homePath = "/dispatch-calendar";
    // Jill's 2026-07-02 consolidation: Travel folded into Dispatch (My
    // Schedule shows own travel; Who's Traveling is a Dispatch tab) and the
    // Filed = finished, read-only records ONLY (Jill 2026-07-22: one tense per
    // page — drafts, new reports, and blank forms all live in Trip Workspace) —
    // plan it, work it, file it.
    tabs = [
      { label: "Dispatch", path: "/dispatch-calendar", icon: "schedule" },
      { label: "Trip Workspace", path: "/trip-workspace", icon: "workspace" },
      { label: "Filed", path: "/paperwork", icon: "paperwork" },
      // Document Q&A, future wiki home — a flagship tab, 4th so it keeps a
      // phone slot (Jill 2026-08-18; label becomes "Wiki" when that ships).
      { label: "Ask", path: "/ask-rules", icon: "ask" },
    ];
    // Admins run the office weekly (dispatch + reservations uploads) — the
    // toolbox earns a visible tab, not a scroll through More (Jill 2026-07-02).
    // 5th: on a phone it lives in the More sheet (Jill's pick 2026-08-18 —
    // Ask keeps the 4th slot).
    if (isAdmin) tabs.push({ label: "Admin", path: "/system-admin", icon: "admin" });
    // Menu labels are short nouns; the page header carries the full name
    // (Jill's 2026-07-15 nav slimming). My Profile lives in the account
    // menu (top right) — it was listed twice.
    const pilotTools = [
      { label: "Vessel Info", path: "/vessel-slips" },
      // The Travel Sheet moved to a Dispatch tab (Jill 2026-08-19 — it was
      // too hidden here); /travel-sheet now redirects there.
      { label: "Ask the Board", path: "/ask-board" },
      { label: "Requests", path: "/pilot-requests" },
      { label: "Continuing Education", path: "/continuing-education" },
      { label: "Sea Service", path: "/sea-service" },
    ];
    moreGroups.push({ label: "Pilot tools", items: pilotTools });
    // Training lives in its own group (Jill 2026-07-10 — it was crowding
    // Pilot tools): a deputy on the tonnage ladder sees their records, a
    // training pilot their grading inbox, a mentor their review list; the
    // opportunities board is there for everyone.
    const trainingTools = [];
    if (hasTrainingTrack || isAdmin) trainingTools.push({ label: "My Training Records", path: "/training-records", attention: "training" });
    // the chair checks paper records for trainees with no mentor there
    if (isTrainingPilot || isAdmin || isChair) {
      trainingTools.push({ label: "TR Grading Queue", path: "/tr-grading", attention: "grading" });
    }
    // The Deputy Organizer belongs here too (Jill 2026-07-27): deputies get
    // neither mentor nor auditor, so the Organizer does their per-TR grade
    // transfer and review. Without this they had no page to do it from.
    const isDeputyOrganizer = (committeePositions || []).includes("deputy_organizer");
    if (isMentor || isDeputyOrganizer || isAdmin) {
      trainingTools.push({ label: isMentor ? "Mentor Review" : "Deputy Review", path: "/mentor-review", attention: "mentor" });
    }
    // Auditors and the Trainee/Deputy Organizers (roster Roles column) get
    // the committee's milestone-verification page (Aaron via Jill 7/18).
    if ((committeePositions || []).some((p) => ["auditor", "trainee_organizer", "deputy_organizer"].includes(p)) || isAdmin || isChair) {
      trainingTools.push({ label: "Committee Review", path: "/committee-review", attention: "committee" });
    }
    // Chair signs pilots' USCG sea service letters
    if (isChair || isAdmin) trainingTools.push({ label: "Sea Service Review", path: "/sea-service-review" });
    // the Vol II numbers the bars count against (C7, item 21)
    if (isChair || isAdmin) trainingTools.push({ label: "Training Requirements", path: "/training-requirements" });
    trainingTools.push({ label: "Training Opportunities", path: "/training-opportunities" });
    // Cargo pool is visible to every pilot (sheet-like transparency, Jill
    // 2026-07-13) — deputies request from it, everyone else can watch.
    trainingTools.push({ label: "Cargo Training Requests", path: "/cargo-pool", attention: "cargo" });
    moreGroups.push({ label: "Training", items: trainingTools });
  } else if (has("trainee")) {
    primary = "trainee";
    homePath = "/training-opportunities";
    // Travel retired here too (2026-07-02): trainees reach Who's Traveling —
    // their ride-coordination board — via the Dispatch page like everyone else.
    tabs = [
      // full words, never "Opps" (Jill 2026-11-03: "No abbreviations")
      { label: "Training Opportunities", path: "/training-opportunities", icon: "opportunities" },
      { label: "My Training", path: "/training-records", icon: "paperwork" },
      // No Trip Workspace (Jill 2026-11-03): practice slips and the training
      // transit brief went unused, and slips and expenses are pilots' only.
      { label: "Ask", path: "/ask-rules", icon: "ask" },
      { label: "Dispatch", path: "/dispatch-calendar", icon: "schedule" },
    ];
    moreGroups.push({ label: "", items: [
      { label: "Cargo Training Requests", path: "/cargo-pool", attention: "cargo" },
      { label: "Vessel Info", path: "/vessel-slips" },
      // Trainee days count toward USCG renewal sea service too (Jill 2026-07-18)
      { label: "Sea Service", path: "/sea-service" },
    ] });
  } else if (has("office")) {
    // Office logins land on a card page built around their jobs (Jill
    // 2026-09-22): publish to the pilots, look something up, get help. The
    // Roster and Vessels tabs went the same day (the roster is a card on
    // the home page; Vessels was the retired CSV diff tool).
    primary = "office";
    homePath = "/office";
    tabs = [
      { label: "Home", path: "/office", icon: "home" },
      { label: "Dispatch", path: "/dispatch-calendar?view=matrix", icon: "schedule" },
    ];
    if (has("reservations")) tabs.push({ label: "Reservations", path: "/reservations-upload", icon: "upload" });
    if (has("scheduler")) tabs.push({ label: "Publish Dispatch", path: "/dispatch-upload", icon: "upload" });
    tabs.push({ label: "Ask", path: "/ask-rules", icon: "ask" });
    // Expense Approvals retired 2026-09-22 (Jill): the office tracks expense
    // reports in its own in-house tool; the portal's job ends at the submit.
  } else if (has("agent")) {
    primary = "agent";
    homePath = "/claa-trainee-queue";
    tabs = [{ label: "Request Queue", path: "/claa-trainee-queue", icon: "queue" }];
  } else if (has("accounting")) {
    primary = "accounting";
    homePath = "/office";
    tabs = [
      { label: "Home", path: "/office", icon: "home" },
      { label: "Dispatch", path: "/dispatch-calendar?view=matrix", icon: "schedule" },
      { label: "Ask", path: "/ask-rules", icon: "ask" },
    ];
  }

  // Positions add items in More, never a separate dashboard.
  if (isBoard) {
    moreGroups.push({
      label: "Board of Directors",
      items: [
        { label: "Board Workspace", path: "/board-workspace" },
        { label: "Board Questions", path: "/board-queue", attention: "board" },
        { label: "Pilot Requests", path: "/bod-requests" },
        { label: "Board Reports", path: "/board-reports" },
        // Aaron via Jill 2026-09-07: dispatch-aware meeting-day finder for
        // the board and committees, deliberately NOT a Dispatch-page tab.
        { label: "Meeting Finder", path: "/meeting-finder" },
      ],
    });
  }
  // Committee members who are not on the board reach the same finder from a
  // Committees group (training positions, exam committee; admins too).
  const isCommittee = cp.some((p) => COMMITTEE_POSITIONS.includes(p)) || pos === "ce_lead";
  if (!isBoard && (isCommittee || isAdmin)) {
    moreGroups.push({ label: "Committees", items: [{ label: "Meeting Finder", path: "/meeting-finder" }] });
  }
  if (isChair) {
    moreGroups.push({
      label: "Training Chair",
      items: [
        { label: "Trainee Audit", path: "/training-chair", attention: "chairReview" },
        { label: "Quarterly to State", path: "/training-quarterly" },
        { label: "CLAA Tracking", path: "/claa-trainee-queue", attention: "chair" },
        { label: "Ride Request Review", path: "/trainee-digest" },
      ],
    });
  }
  if (isCeAdmin && !isAdmin) {
    moreGroups.push({
      label: "CE Administration",
      items: [
        { label: "CE Audits & Compliance", path: "/ce-audits" },
        { label: "Manage CE Courses", path: "/manage-ce-courses" },
      ],
    });
  }

  // Secondary roles merge in as groups (a pilot who also works the office
  // keeps pilot tabs and gets the office tools here).
  if (has("office") && primary !== "office" && !isAdmin) {
    const items = [
      { label: "Office home", path: "/office" },
      { label: "Pilot Roster", path: "/pilot-roster" },
      { label: "Contact Directory", path: "/contact-directory" },
    ];
    if (has("reservations")) items.push({ label: "Upload Reservations", path: "/reservations-upload" });
    if (has("scheduler")) items.push({ label: "Publish Dispatch", path: "/dispatch-upload" });
    moreGroups.push({ label: "Office", items });
  }
  // The directory's other roles (Codex on #522): a dispatch login, or an
  // accounting login whose tabs are a pilot's or agent's, has no office home
  // to find the card on, so the link rides in More.
  if ((has("dispatch") || (has("accounting") && primary !== "accounting")) && !has("office") && !isAdmin) {
    moreGroups.push({ label: "Office", items: [{ label: "Contact Directory", path: "/contact-directory" }] });
  }
  if (has("agent") && primary !== "agent" && !isChair) {
    moreGroups.push({ label: "Agent", items: [{ label: "CLAA Tracking", path: "/claa-trainee-queue" }] });
  }
  // Slip coordinators (Tyler, Nathan): after a CLAA calendar import they
  // review the season's pre-built slips and hunt for one-offs (the World /
  // Star Seeker class of surprises) before pilots ever see them. Season Slips
  // is where they approve, flag, answer and download (Jill 2026-09-21). The
  // old Batch Slip Generator page was deleted the same day.
  // A roster POSITION since 2026-08-21 (Jill: it's a pilot role), not a
  // user_roles role — assigned via the Pilot Roster's position toggles.
  if (cp.includes("slip_coordinator") && !isAdmin) {
    moreGroups.push({
      label: "Slip coordinator",
      items: [
        { label: "Season Slips 2027", path: "/batch-slips" },
        { label: "Transit Brief", path: "/voyage-plan" },
        { label: "New Pilot Slip", path: "/pilot-slip" },
      ],
    });
  }

  if (isAdmin) {
    // Admins reach Season Slips here too (Jill is an admin, not a slip
    // coordinator on the roster).
    moreGroups.push({ label: "Slips", items: [{ label: "Season Slips 2027", path: "/batch-slips" }] });
    // The Admin tab lands on the toolbox page, which cards every admin tool
    // (users, roster, vessels, scheduling, training, CE). More keeps only
    // the weekly-rhythm items — the 13-item, 3-group pile was two lists of
    // the same tools (Jill's 2026-07-15 nav slimming).
    moreGroups.push({
      label: "Admin · Weekly ops",
      items: [
        { label: "Publish Dispatch", path: "/dispatch-upload" },
        { label: "Upload Reservations", path: "/reservations-upload" },
      ],
    });
  }

  // Every login gets the running What's New tally (release notes + From the
  // Developer). The badge counts unseen entries — TopNav resolves it from
  // localStorage, no network.
  // Help Videos shows only when this login has a video to watch (Jill
  // 2026-11-02), so the office never sees the item.
  const portalItems = [{ label: "What's New", path: "/whats-new", attention: "whatsnew" }];
  // Help (the step-by-step guides, Jill 2026-11-03) for trainees and pilots
  if (helpGuidesFor(r).length > 0) portalItems.push({ label: "Help", path: "/help" });
  if (helpVideosFor(r).length > 0) portalItems.push({ label: "Help Videos", path: "/help-videos" });
  moreGroups.push({ label: "Portal", items: portalItems });

  return { tabs, moreGroups, homePath };
}
